import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import dsa1Image from '../assets/products/dsa1.png';
import dsa2Image from '../assets/products/dsa2.jpeg';
import {
  FiDownload,
  FiCheck,
  FiAlertCircle,
  FiX,
  FiCheckCircle,
  FiArrowRight,
  FiClock,
  FiDollarSign,
  FiTrendingUp,
  FiShield,
  FiZap,
  FiTool,
} from 'react-icons/fi';
import {
  BsDroplet,
  BsShieldCheck,
  BsLightning,
} from 'react-icons/bs';
import { GiChemicalDrop, GiWaterDrop } from 'react-icons/gi';
import InquiryModal from '../components/InquiryModal';
import ruDsa6Brochure from '../assets/pdf/Pamphletrudsa6.pdf';
import ContactFormModal from '../components/ContactFormModal';

const ProductRUDSA6 = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [downloadType, setDownloadType] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleDownloadClick = (type) => {
    setDownloadType(type);
    setModalOpen(true);
  };

  const problemPoints = [
    {
      title: 'Reduced Efficiency',
      desc: 'They severely limit the functioning of equipment and sub-assemblies',
      icon: FiTrendingUp,
    },
    {
      title: 'Wasted Resources',
      desc: 'They cause a direct increase in fuel consumption',
      icon: FiDollarSign,
    },
    {
      title: 'Costly Damage',
      desc: 'They increase wear and tear and can lead to equipment damage',
      icon: FiAlertCircle,
    },
  ];

  const traditionalMethodFlaws = [
    {
      title: 'Manual Cleaning Flaw',
      desc: 'Current methods are uneconomical, involving considerable time, manpower, and often fail to remove scales completely',
      icon: FiClock,
    },
    {
      title: 'HCL Risk',
      desc: 'Using water base solution like Hydrochloric Acid (HCL) not only loosens scales only to a certain extent but also erodes the metal surface, actively reducing the life of your equipment',
      icon: FiAlertCircle,
    },
    {
      title: 'Incomplete Removal',
      desc: 'Approximately 80% of scales are in the water jacket, which currently requires demounting the engine just to attempt manual cleaning, yet scales are still not removed completely',
      icon: FiX,
    },
  ];

  const whyRuDsa6Superior = [
    {
      title: 'Complete Scale Dissolution',
      desc: 'The unique formula dissolves scales completely',
      icon: BsDroplet,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Non-Corrosive Safety',
      desc: 'It does not react with any metal, rubber, or PVC. It is non-flammable and non-corrosive',
      icon: BsShieldCheck,
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Harmless Composition',
      desc: 'It is totally harmless to human beings, plants and the environment',
      icon: FiShield,
      gradient: 'from-amber-500 to-orange-500',
    },
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Empty',
      desc: 'Drain the existing liquid from the setup',
      icon: '🚰',
    },
    {
      step: '2',
      title: 'Fill & Soak',
      desc: 'Fill the setup with RU DSA-6 and leave it for 6-8 hours',
      icon: '⏰',
    },
    {
      step: '3',
      title: 'Circulate',
      desc: 'Start the engine for 10-20 minutes, allowing RU DSA-6 to circulate and dissolve the scales',
      icon: '🔄',
    },
    {
      step: '4',
      title: 'Flush',
      desc: 'Flush the system with clean water to achieve a scales-free setup',
      icon: '✨',
    },
  ];

  const comparisonData = [
    {
      feature: 'Demounting Required?',
      oldMethod: 'Yes, for radiators and water jackets',
      ruDsa6: 'No need to demount the radiator or detach the affected part',
      advantage: true,
    },
    {
      feature: 'Corrosion Risk?',
      oldMethod: 'High (HCL erodes metal)',
      ruDsa6: 'None (Non-corrosive)',
      advantage: true,
    },
    {
      feature: 'Man-Hours Involved?',
      oldMethod: 'Involvement of labour for long hours',
      ruDsa6: 'Reduced by up to 80% (almost negligible)',
      advantage: true,
    },
  ];

  const benefits = [
    {
      title: 'Life Extension',
      desc: 'Enhances the life of engine equipment and machines by up to 50%',
      icon: FiTrendingUp,
      color: 'text-green-600',
      bgColor: 'from-green-500 to-emerald-500',
    },
    {
      title: 'Fuel Savings',
      desc: 'Improves fuel efficiency',
      icon: FiZap,
      color: 'text-blue-600',
      bgColor: 'from-blue-500 to-cyan-500',
    },
    {
      title: 'Time & Labor Savings',
      desc: 'Saves working man-hours and working time',
      icon: FiClock,
      color: 'text-purple-600',
      bgColor: 'from-purple-500 to-pink-500',
    },
    {
      title: 'Complete Clean',
      desc: 'Removes scales completely, giving a scales-free radiator and water jacket',
      icon: FiCheckCircle,
      color: 'text-cyan-600',
      bgColor: 'from-cyan-500 to-blue-500',
    },
  ];

  const equipmentProtected = [
    'Compressors',
    'Oil Coolers',
    'Chillers',
    'Condensers',
    'Water Tanks',
    'Radiators',
    'Heat Exchangers',
  ];

  // Molecular pattern
  const MolecularPattern = () => (
    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="molecule-dsa6" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.3" />
          <circle cx="80" cy="40" r="4" fill="currentColor" opacity="0.2" />
          <circle cx="140" cy="70" r="2" fill="currentColor" opacity="0.4" />
          <line x1="20" y1="20" x2="80" y2="40" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#molecule-dsa6)" />
    </svg>
  );

  return (
    <div className="min-h-screen overflow-hidden bg-white">
      {/* Section 1: Hero Banner & Core Promise */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-32 pb-12">
        {/* Dynamic Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-200 via-cyan-100 to-blue-100">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(6, 182, 212, 0.4), transparent 50%)`,
            }}
          />
        </div>

        {/* Molecular Pattern */}
        <div className="absolute inset-0 text-cyan-400 opacity-20">
          <MolecularPattern />
        </div>

        {/* Floating droplets */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -70, 0],
                x: [0, Math.random() * 50 - 25, 0],
                scale: [1, 1.4, 1],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 7 + Math.random() * 3,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            >
              <GiWaterDrop className="w-8 h-8 text-cyan-400/50" />
            </motion.div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-6xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center space-x-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-xl px-6 py-3 rounded-full text-black mb-8 border border-cyan-400/30"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <GiChemicalDrop className="text-cyan-600 text-2xl animate-pulse" />
              <span className="font-semibold">Flagship De-Scaling Solution</span>
            </motion.div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-vp-grey-900 mb-6 leading-tight">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-vp-grey-800 via-vp-grey-900 to-vp-grey-800">
                RU DSA-6
              </span>
              <span className="block text-3xl md:text-4xl lg:text-5xl mt-4 text-vp-grey-800">
                The Eco-Safe Solution for
              </span>
              <span className="block text-4xl md:text-5xl lg:text-6xl text-vp-grey-800">
                Complete Scale Removal
              </span>
            </h1>

            {/* Sub-headline */}
            <motion.p
              className="text-xl md:text-2xl lg:text-3xl text-vp-grey-800 mb-12 max-w-5xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Restore peak operational efficiency and extend equipment life by up to{' '}
              <span className="text-vp-grey-900 font-bold">50%</span>—without costly disassembly or corrosive water base solution.
            </motion.p>

            {/* CTAs */}
            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <button
                onClick={() => setContactModalOpen(true)}
                className="group px-12 py-6 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-xl rounded-full hover:shadow-2xl hover:shadow-cyan-500/50 transform hover:scale-105 transition-all duration-300 flex items-center space-x-3"
              >
                <span>Request a Quote</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>

              <button
                onClick={() => handleDownloadClick('brochure')}
                className="group px-12 py-6 bg-white/10 backdrop-blur-md text-vp-grey-800 font-bold text-xl rounded-full border-2 border-white/30 hover:bg-white hover:text-blue-900 transition-all duration-300 flex items-center space-x-3"
              >
                <FiDownload className="text-2xl" />
                <span>Download Brochure</span>
              </button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              className="mt-16 flex flex-wrap items-center justify-center gap-8 text-sm text-black"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-cyan-400" />
                <span>100% Eco-Safe</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-cyan-400" />
                <span>Non-Corrosive</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-cyan-400" />
                <span>No Demounting Required</span>
              </div>
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="text-cyan-400" />
                <span>50% Life Extension</span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-8 h-14 border-2 border-white/50 rounded-full flex justify-center p-2">
            <motion.div 
              className="w-2 h-4 bg-gradient-to-b from-cyan-400 to-blue-400 rounded-full"
              animate={{ y: [0, 20, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Section 2: The Problem with Traditional De-Scaling */}
      <section id="problem" className="py-24 md:py-32 bg-gradient-to-br from-red-50 via-orange-50 to-red-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-96 h-96 bg-red-200 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl" />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.div
                className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6 border-4 border-red-200"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FiAlertCircle className="w-10 h-10 text-red-600" />
              </motion.div>

              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                The Problem with{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-600">
                  Traditional De-Scaling
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-black max-w-4xl mx-auto leading-relaxed">
                Scales—mineral deposits formed from continuous contact with water—are the{' '}
                <span className="font-bold text-red-600">silent killers</span> of your machinery.
                As bad conductors, scales cause catastrophic ripple effects across your operations:
              </p>
            </div>

            {/* Problem Points */}
            <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
              {problemPoints.map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10 }}
                  className="bg-white rounded-3xl p-8 shadow-xl border-2 border-red-200 hover:border-red-400 transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6">
                    <point.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{point.title}</h3>
                  <p className="text-black leading-relaxed text-lg">{point.desc}</p>
                </motion.div>
              ))}
            </div>

            {/* Traditional Method Flaws */}
            <div className="max-w-5xl mx-auto">
              <h3 className="text-4xl font-black text-center text-slate-900 mb-12">
                The Flaws in Current Methods
              </h3>
              
              <div className="space-y-6">
                {traditionalMethodFlaws.map((flaw, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-red-500 hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex items-start space-x-6">
                      <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <flaw.icon className="w-7 h-7 text-red-600" />
                      </div>
                      <div>
                        <h4 className="text-2xl font-bold text-slate-900 mb-3">{flaw.title}</h4>
                        <p className="text-black leading-relaxed text-lg">{flaw.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <motion.div
              className="text-center mt-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <a
                href="#solution"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <span>Stop Wasting Time and Fuel—See the Solution</span>
                <FiArrowRight className="text-2xl" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: The VP Chem Technologies Solution */}
      <section id="solution" className="py-24 md:py-32 bg-gradient-to-br from-cyan-50 via-blue-50 to-sky-50 text-vp-grey-900 relative overflow-hidden bg-pattern-grid">
        {/* Animated grid */}
        <div className="absolute inset-0 opacity-5">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(6, 182, 212, 0.3) 1px, transparent 1px),
                                linear-gradient(to bottom, rgba(6, 182, 212, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-cyan-600/10 backdrop-blur-xl rounded-full text-cyan-700 font-semibold mb-6 border border-cyan-600/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                💧 The VP Chem Technologies Solution
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                  RU DSA-6
                </span>
                <br />
                <span className="text-vp-grey-800 text-4xl md:text-5xl">Superior by Design</span>
              </h2>
              
              <p className="text-xl md:text-2xl text-vp-grey-800 max-w-4xl mx-auto leading-relaxed">
                RU DSA-6 is a proprietary formula specifically engineered for industrial strength scale removal without the risks associated with corrosive acids or the inefficiency of manual methods.
              </p>
            </div>

            {/* Product Image */}
            <motion.div
              className="max-w-4xl mx-auto mb-16"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-cyan-200">
                <img 
                  src={dsa1Image} 
                  alt="RU DSA-6 Product" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>

            {/* Why Superior Cards */}
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
              {whyRuDsa6Superior.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  whileHover={{ y: -10 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white backdrop-blur-xl border-2 border-cyan-200 rounded-3xl p-8 h-full group-hover:bg-cyan-50 group-hover:border-cyan-400 transition-all duration-500 shadow-lg">
                    <motion.div
                      className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <item.icon className="w-10 h-10 text-white" />
                    </motion.div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-vp-grey-900 mb-4">
                      {item.title}
                    </h3>
                    <p className="text-vp-grey-800 leading-relaxed text-lg">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => handleDownloadClick('datasheet')}
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-lg rounded-full hover:shadow-xl transform hover:scale-105 transition-all duration-300 shadow-lg"
              >
                <FiDownload className="text-2xl" />
                <span>Download RU DSA-6 Technical Brochure</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 4: Simple, Non-Invasive Application */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-100/50 to-transparent rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-100 to-blue-100 rounded-full text-cyan-700 font-semibold mb-6"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🔧 Simple Application Process
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
                Simple, Non-Invasive{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                  Application
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-black max-w-4xl mx-auto leading-relaxed">
                The single greatest benefit of RU DSA-6 is its ability to{' '}
                <span className="font-bold text-cyan-600">clean your equipment without detaching the affected part</span> from the machine.
              </p>
            </div>

            {/* Product Image 2 */}
            <motion.div
              className="max-w-4xl mx-auto mb-12"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-cyan-200">
                <img 
                  src={dsa2Image} 
                  alt="RU DSA-6 Application Process" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>

            {/* 4-Step Process */}
            <div className="max-w-5xl mx-auto mb-16">
              <h3 className="text-4xl font-black text-center text-slate-900 mb-12">
                The 4-Step Process
              </h3>
              
              <div className="grid md:grid-cols-2 gap-8">
                {processSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    className="relative group"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 opacity-10 group-hover:opacity-20 rounded-3xl blur-xl transition-opacity" />
                    
                    <div className="relative bg-white rounded-3xl p-8 shadow-xl border-2 border-cyan-200 group-hover:border-cyan-400 transition-all duration-300">
                      <div className="flex items-start space-x-6">
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl font-black mb-4">
                            {step.step}
                          </div>
                          <div className="text-5xl text-center">{step.icon}</div>
                        </div>
                        
                        <div className="flex-1">
                          <h4 className="text-2xl font-bold text-slate-900 mb-3">{step.title}</h4>
                          <p className="text-black leading-relaxed text-lg">{step.desc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Comparison Table */}
            <div className="max-w-6xl mx-auto">
              <h3 className="text-4xl font-black text-center text-slate-900 mb-12">
                Old Method vs. RU DSA-6
              </h3>
              
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-2 border-cyan-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                        <th className="px-6 py-6 text-left text-lg font-bold">Feature</th>
                        <th className="px-6 py-6 text-left text-lg font-bold">Old Method (Manual/HCL)</th>
                        <th className="px-6 py-6 text-left text-lg font-bold">RU DSA-6 Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.map((row, index) => (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className={`${index % 2 === 0 ? 'bg-cyan-50' : 'bg-white'} hover:bg-cyan-100 transition-colors`}
                        >
                          <td className="px-6 py-6 font-semibold text-slate-900 text-lg border-b border-cyan-200">
                            {row.feature}
                          </td>
                          <td className="px-6 py-6 text-black border-b border-cyan-200">
                            <div className="flex items-start space-x-2">
                              <FiX className="text-red-500 mt-1 flex-shrink-0 text-xl" />
                              <span>{row.oldMethod}</span>
                            </div>
                          </td>
                          <td className="px-6 py-6 text-black border-b border-cyan-200">
                            <div className="flex items-start space-x-2">
                              <FiCheck className="text-green-500 mt-1 flex-shrink-0 text-xl" />
                              <span className="font-semibold text-green-700">{row.ruDsa6}</span>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* CTA */}
            <motion.div
              className="text-center mt-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-lg rounded-full hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <FiDollarSign className="text-2xl" />
                <span>Calculate Your Cost Savings—Contact Us Today</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Section 5: Unbeatable Benefits & Results */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-sky-100 via-cyan-50 to-blue-100 relative overflow-hidden">
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-blob" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Section Header */}
            <div className="text-center mb-16">
              <motion.span
                className="inline-block px-6 py-2 bg-cyan-600/10 backdrop-blur-xl rounded-full text-cyan-700 font-semibold mb-6 border border-cyan-600/30"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring" }}
              >
                🎯 Unbeatable Benefits
              </motion.span>
              
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black mb-8">
                Quantifiable ROI &{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                  Long-Term Value
                </span>
              </h2>
              
              <p className="text-xl md:text-2xl text-vp-grey-800 max-w-4xl mx-auto leading-relaxed">
                Implementing RU DSA-6 is an investment in the{' '}
                <span className="text-cyan-700 font-bold">longevity and efficiency</span> of your assets
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto mb-16">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, scale: 1.03 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${benefit.bgColor} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
                  
                  <div className="relative bg-white backdrop-blur-xl border-2 border-cyan-200 rounded-3xl p-8 h-full group-hover:bg-cyan-50 group-hover:border-cyan-400 transition-all duration-500 shadow-lg">
                    <motion.div
                      className={`w-16 h-16 bg-gradient-to-br ${benefit.bgColor} rounded-2xl flex items-center justify-center mb-6`}
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <benefit.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    
                    <h3 className="text-2xl font-bold text-vp-grey-900 mb-4">
                      {benefit.title}
                    </h3>
                    <p className="text-vp-grey-800 leading-relaxed">
                      {benefit.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Equipment Protected */}
            <div className="max-w-5xl mx-auto">
              <h3 className="text-4xl font-black text-center mb-12 text-vp-grey-900">
                Equipment Protected by{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
                  RU DSA-6
                </span>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {equipmentProtected.map((equipment, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="bg-white backdrop-blur-md rounded-2xl p-6 border-2 border-cyan-200 hover:border-cyan-400 transition-all duration-300 text-center shadow-md"
                  >
                    <FiCheckCircle className="w-12 h-12 text-cyan-600 mx-auto mb-3" />
                    <p className="text-vp-grey-800 font-semibold text-lg">{equipment}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <motion.div
              className="text-center mt-16"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => handleDownloadClick('technical-datasheet')}
                className="group inline-flex items-center space-x-3 px-10 py-5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold text-lg rounded-full hover:shadow-xl transform hover:scale-105 transition-all duration-300 shadow-lg"
              >
                <FiDownload className="text-2xl" />
                <span>Download RU DSA-6 Technical Data Sheet</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 relative overflow-hidden">
        {/* Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-white rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3,
              }}
            />
          ))}
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-8">
              Ready to Restore Your Equipment?
            </h2>
            
            <p className="text-xl md:text-2xl text-white mb-12 max-w-3xl mx-auto">
              Experience the power of RU DSA-6 and transform your maintenance operations today
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <button
                onClick={() => setContactModalOpen(true)}
                className="group w-full sm:w-auto px-12 py-6 bg-white text-cyan-600 font-bold text-xl rounded-2xl hover:bg-slate-100 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center space-x-3"
              >
                <span>Request a Quote Today</span>
                <FiArrowRight className="group-hover:translate-x-2 transition-transform" />
              </button>
              
              <button
                onClick={() => handleDownloadClick('brochure')}
                className="group w-full sm:w-auto px-12 py-6 bg-transparent border-3 border-white text-white font-bold text-xl rounded-2xl hover:bg-white hover:text-cyan-600 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-3"
              >
                <FiDownload className="text-2xl" />
                <span>Download Brochure</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Inquiry Modal */}
      <InquiryModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)}
        downloadType={downloadType}
        productName="RU DSA-6"
        downloadUrl={ruDsa6Brochure}
      />

      {/* Contact Form Modal */}
      <ContactFormModal 
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        productName="RU DSA-6"
      />
    </div>
  );
};

export default ProductRUDSA6;



